import { useEffect, useMemo, useState } from 'react';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { useDesignerView } from '../../../UserInterfaceDesigner/Contexts/DesignerViewContext';
import { IComponentModel } from '../../../UserInterfaceDesigner/Models/IComponentModel';
import { IContainerComponentModel } from '../../../UserInterfaceDesigner/Models/IContainerComponentModel';
import { ComponentRefModel } from '../../Models/ComponentRefModel';
import ListWithFilter from '../ListWithFilter';

type ComponentInputDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	componentRef: ComponentRefModel | undefined;
	onComponentRefChange: (componentRef: ComponentRefModel | undefined) => void;
};

type ComponentWithPath = {
	Id: string;
	Type: string;
	Path: string;
	component: IComponentModel;
};

function getAllComponents(container: IContainerComponentModel, pathPrefix: string = ''): ComponentWithPath[] {
	const results: ComponentWithPath[] = [];

	// Add the container itself if it has an ID
	if (container.Id) {
		results.push({
			Id: container.Id,
			Type: container.Type,
			Path: pathPrefix || 'Root',
			component: container,
		});
	}

	// Recursively add all children
	if (container.Children && Array.isArray(container.Children)) {
		container.Children.forEach((child) => {
			const childPath = pathPrefix ? `${pathPrefix} > ${child.Type}` : child.Type;
			results.push({
				Id: child.Id,
				Type: child.Type,
				Path: childPath,
				component: child,
			});

			// If child is also a container, recurse
			const containerChild = child as IContainerComponentModel;
			if (containerChild.Children && Array.isArray(containerChild.Children)) {
				results.push(...getAllComponents(containerChild, childPath));
			}
		});
	}

	return results;
}

export function ComponentInputDialog({ open, setOpen, componentRef, onComponentRefChange }: ComponentInputDialogProps) {
	const [selectedId, setSelectedId] = useState<string | undefined>(componentRef?.Id);
	const designerView = useDesignerView();

	// Get components from the current view or shared component being edited
	const allComponents = useMemo(() => {
		const currentView =
			designerView.view ||
			(designerView.sharedComponent
				? ({ ...designerView.sharedComponent.Component, Title: designerView.sharedComponent.Name } as IContainerComponentModel)
				: null);

		if (!currentView) return [];

		return getAllComponents(currentView as IContainerComponentModel, designerView.view?.Title || designerView.sharedComponent?.Name || 'Component');
	}, [designerView]);

	useEffect(() => {
		if (open) {
			setSelectedId(componentRef?.Id);
		}
	}, [open, componentRef]);

	const handleSelect = (componentWithPath: ComponentWithPath) => {
		setSelectedId(componentWithPath.Id);
	};

	const handleSave = () => {
		if (selectedId) {
			const selected = allComponents.find((c) => c.Id === selectedId);
			onComponentRefChange({
				Id: selectedId,
				Name: selected?.Type,
			});
		}
		setOpen(false);
	};

	return (
		<Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
			<DialogTitle>Select Component</DialogTitle>
			<DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
				{allComponents.length > 0 ? (
					<ListWithFilter
						items={allComponents}
						selectedItemId={selectedId}
						onSelected={handleSelect}
						placeholder="Search components"
						getDisplayName={(component) => component.Type}
						getDescription={(component) => component.Path}
						getKey={(component) => component.Id}
						settingsKey="componentInputDialog"
						showDescriptionToggle
						sx={{ flex: 1, minHeight: 0 }}
					/>
				) : (
					<Box sx={{ p: 2, textAlign: 'center' }}>
						<Typography color="text.secondary">No components available in the current view. Please add components to this view first.</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setOpen(false)}>Cancel</Button>
				<Button onClick={handleSave} variant="contained" disabled={!selectedId}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}
